/**
 * Process-wide cache for the menu categories query that the default layout runs on every SSR render.
 *
 * The SSR bundle runs in a new context per request, so VSF's own elasticCache and any module state
 * start empty, and every uncached page (404s included) fetched ~1200 categories from the API again.
 * This cache is kept on the Express app, which outlives the render context. Responses are stored as
 * JSON and every render parses its own copy: Vuex makes state objects reactive, and category objects
 * shared between stores would keep every past render's store in memory.
 */

const LOCALS_KEY = 'menuCategoriesCache'
const MS_IN_SECOND = 1000
const CATEGORY_TAG_REGEX = /^(C|C_ALL|C\d+|category)$/

interface CacheEntry {
  expiresAt: number,
  json: Promise<string>
}

export type MenuCategoriesCache = Map<string, CacheEntry>

/**
 * @param {object} app Express app (`ssrContext.server.app` inside the SSR bundle)
 * @returns {MenuCategoriesCache|null} null when there is no app, e.g. in the browser
 */
export function getMenuCategoriesCache (app): MenuCategoriesCache | null {
  if (!app || !app.locals) {
    return null
  }

  if (!app.locals[LOCALS_KEY]) {
    app.locals[LOCALS_KEY] = new Map()
  }

  return app.locals[LOCALS_KEY]
}

/**
 * Returns a fresh copy of the cached response, or calls the fetcher once and caches its result.
 * Renders that ask while the fetch is running wait for the same request. Failed fetches and
 * responses rejected by `isCacheable` are not kept.
 */
export async function fetchWithMenuCategoriesCache<T> (
  cache: MenuCategoriesCache,
  key: string,
  ttlSeconds: number,
  fetcher: () => Promise<T>,
  isCacheable: (result: T) => boolean = () => true
): Promise<T> {
  const now = Date.now()
  let entry = cache.get(key)

  if (!entry || entry.expiresAt <= now) {
    const newEntry: CacheEntry = { expiresAt: now + ttlSeconds * MS_IN_SECOND, json: null }
    const evict = () => {
      if (cache.get(key) === newEntry) {
        cache.delete(key)
      }
    }

    newEntry.json = fetcher().then(result => {
      if (!isCacheable(result)) {
        evict()
      }

      return JSON.stringify(result)
    })
    newEntry.json.catch(evict)
    cache.set(key, newEntry)
    entry = newEntry
  }

  return JSON.parse(await entry.json)
}

/**
 * Drops the cached menu when an output cache invalidation touches categories.
 *
 * @param {object} app Express app
 * @param {string[]} tags invalidated tags, with `*` already expanded
 * @returns {boolean} true when the cache was cleared
 */
export function clearMenuCategoriesCache (app, tags: string[]): boolean {
  const cache = app && app.locals && app.locals[LOCALS_KEY]

  if (!cache || !tags.some(tag => CATEGORY_TAG_REGEX.test(tag))) {
    return false
  }

  cache.clear()

  return true
}
